/**
 * How a session override gets its Zoom link — the one place that answers
 * "which link does this special session actually use, and does it still owe us
 * one?".
 *
 * Why this exists: a blank `zoomJoinUrl` used to mean two different things at
 * once — "deliberately reuse the class's usual link" and "we intend a different
 * link, we just don't have it yet". Those collapse into identical stored data,
 * so nobody could chase the second case, and the first case looked unfinished.
 * The mode makes the admin's intent explicit and stored:
 *
 *   'inherit' → use the class's default link. Nothing outstanding.
 *   'custom'  → this session has its own link, and it is present.
 *   'pending' → this session gets its OWN link, not decided yet. Deliberately
 *               does NOT fall back to the class link: falling back would
 *               re-merge the two states at the exact moment it matters (join
 *               time) and quietly send students to a room the teacher left.
 *
 * Legacy rows have no mode. `resolveSessionZoomLinkMode` derives one from the
 * data (url present → 'custom', else 'inherit'), which is exactly what blank
 * meant before, so nothing pre-existing changes behaviour.
 *
 * Distinct from `isAwaitingTeacherZoomLink` in ./zoomAccountOptions: that one
 * is derived from a Personal Zoom account (the TEACHER owes the link), and
 * applies to a class default as well as a session. 'pending' is an admin's
 * explicit "a link is coming for this one session", on any account.
 */

export const SESSION_ZOOM_LINK_MODES = [
  'inherit',
  'custom',
  'pending',
] as const;

export type SessionZoomLinkMode = (typeof SESSION_ZOOM_LINK_MODES)[number];

export const DEFAULT_SESSION_ZOOM_LINK_MODE: SessionZoomLinkMode = 'inherit';

/** Shape any caller can satisfy — Mongoose subdoc, API JSON, or form row. */
export interface SessionZoomLinkSource {
  zoomJoinUrl?: string | null;
  zoomLinkMode?: string | null;
}

export function isSessionZoomLinkMode(
  value: unknown
): value is SessionZoomLinkMode {
  return (
    typeof value === 'string' &&
    (SESSION_ZOOM_LINK_MODES as readonly string[]).includes(value)
  );
}

const hasText = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim() !== '';

/**
 * The override's mode, falling back to what a blank URL used to mean. Never
 * throws and never returns null: every override has a resolvable intent, so
 * callers can't accidentally branch on "unset".
 */
export function resolveSessionZoomLinkMode(
  override: SessionZoomLinkSource | null | undefined
): SessionZoomLinkMode {
  if (!override) {
    return DEFAULT_SESSION_ZOOM_LINK_MODE;
  }
  if (isSessionZoomLinkMode(override.zoomLinkMode)) {
    return override.zoomLinkMode;
  }
  return hasText(override.zoomJoinUrl) ? 'custom' : 'inherit';
}

export interface ResolvedSessionZoomLink {
  mode: SessionZoomLinkMode;
  /** Link to actually join with; empty string when one is still owed. */
  effectiveZoomUrl: string;
  /** Zoom account that goes with the effective link. */
  effectiveNotes: string | null;
  /** True when the session runs on the class's usual link. */
  usesDefaultZoomUrl: boolean;
  /** True when this session has its own link and it differs from the class's. */
  hasDifferentLink: boolean;
  /** True when a link was promised for this session and hasn't arrived. */
  isPendingLink: boolean;
}

/**
 * Resolve one override against its class defaults.
 *
 * 'custom' with an empty URL is treated as 'pending' rather than silently
 * inheriting: the admin said this session needs its own link, so a missing one
 * is an outstanding task, not a fallback. (The write path rejects that combo;
 * this keeps reads of any already-stored row honest.)
 */
export function resolveSessionZoomLink(
  override: SessionZoomLinkSource | null | undefined,
  baseZoomUrl: string | null | undefined,
  baseNotes?: string | null,
  overrideNotes?: string | null
): ResolvedSessionZoomLink {
  const mode = resolveSessionZoomLinkMode(override);
  const overrideUrl = hasText(override?.zoomJoinUrl)
    ? override!.zoomJoinUrl!.trim()
    : '';
  const baseUrl = hasText(baseZoomUrl) ? baseZoomUrl.trim() : '';

  if (mode === 'inherit') {
    return {
      mode,
      effectiveZoomUrl: baseUrl,
      effectiveNotes: baseNotes ?? null,
      usesDefaultZoomUrl: true,
      hasDifferentLink: false,
      isPendingLink: false,
    };
  }

  // 'custom' and 'pending' both stand on their own link, so they keep their own
  // Zoom account even before the link lands — that account is what tells ops
  // (and the relief teacher) where the link is supposed to come from.
  const ownNotes = hasText(overrideNotes) ? overrideNotes : (baseNotes ?? null);

  if (mode === 'pending' || overrideUrl === '') {
    return {
      mode: 'pending',
      effectiveZoomUrl: '',
      effectiveNotes: ownNotes,
      usesDefaultZoomUrl: false,
      hasDifferentLink: false,
      isPendingLink: true,
    };
  }

  return {
    mode: 'custom',
    effectiveZoomUrl: overrideUrl,
    effectiveNotes: hasText(overrideNotes) ? overrideNotes : null,
    usesDefaultZoomUrl: false,
    hasDifferentLink: overrideUrl !== baseUrl,
    isPendingLink: false,
  };
}

/** Short label for the mode, for chips and summaries. */
export function describeSessionZoomLinkMode(mode: SessionZoomLinkMode): string {
  switch (mode) {
    case 'inherit':
      return 'Default Zoom link';
    case 'custom':
      return 'Own Zoom link';
    case 'pending':
      return 'Zoom link to come';
  }
}
